import styles from "./defineDefaultValues.module.scss";
import { ModalWindow } from "../../../shared/components/modalWindow/modalWindow";
import { useTranslation } from "react-i18next";
import {
  array,
  number,
  object,
  string
} from "yup";
import {
  useFieldArray,
  useForm
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useCallback,
  useMemo
} from "react";
import {
  CreateVariable,
  CreateVariableFirstStep
} from "../../../types/variableType";
import {
  Dropdown,
  DropdownItem
} from "../../../shared/components/dropdown/dropdown";
import { Environment } from "../../../types/environmentTypes";

type DefineDefaultValuesProps = {
  firstStepVariable: CreateVariableFirstStep;
  onClose: (newVariable?: CreateVariable) => void;
  environments?: Array<Environment>;
}

export const DefineDefaultValues = ({ onClose, firstStepVariable, environments }: DefineDefaultValuesProps) => {
  const { t } = useTranslation();

  const schema = object({
    environmentValues: array().required().of(
      object().shape({
        environmentId: number().required(t("common.notValid")),
        variableValue: string().required(t("common.notValid"))
      })
    )
  });

  const defaultValues = useMemo(() => {
    if (environments) {
      return {
        environmentValues: environments.map(environment => ({
          environmentId: environment.id,
          variableValue: ""
        }))
      };
    }
  }, [environments]);

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    mode: "onChange"
  });
  const { fields: environmentValuesFields } = useFieldArray({ control, name: "environmentValues" });

  const onClickSave = useCallback(() => {
    handleSubmit((result) => {
      onClose({
        ...firstStepVariable,
        ...result
      });
    })();
  }, [handleSubmit, onClose, firstStepVariable]);

  const getEnvironmentName = useCallback((environmentId: number) => {
    return environments?.find(item => item.id === environmentId)?.name;
  }, [environments]);

  const dropdownItems: Array<DropdownItem> = useMemo(() => {
    return firstStepVariable.values.map(value => ({
      id: value.value,
      name: value.value,
      title: value.value
    }));
  }, [firstStepVariable]);

  return (
    <ModalWindow
      title={t("variableManager.defineDefaultValues")}
      onClose={() => onClose()}
      width={380}
      children={
        <div className={styles.container}>
          <div className={styles.header}>
            {t("variableManager.variable")}:
            <span className={styles.variableName}>
              {firstStepVariable.name}
            </span>
          </div>
          <div className={styles.values}>
            <div className={styles.title}>
              {t("testCaseTemplate.environments")}
            </div>
            { environmentValuesFields.map((environmentValueField, index) => (
              <div className={styles.item}
                   key={environmentValueField.id}>
                <div className={styles.label}>
                  {getEnvironmentName(environmentValueField.environmentId)}
                </div>
                <div className={styles.inputContainer}>
                  <div className={styles.dropdown}>
                    <Dropdown control={control}
                              items={dropdownItems}
                              name={`environmentValues.${index}.variableValue`}/>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.buttons}>
            <button className={styles.save}
                    onClick={onClickSave}>{t("common.save")}</button>
          </div>
        </div>
      }
    />
  );
}